package dev.lingvo.site.components.studio.editor

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.tr
import org.jetbrains.compose.web.dom.Canvas
import org.jetbrains.compose.web.dom.Div
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.i18n.trf

/** Only a stable controller enters this composition; pointer samples do not subscribe it to UI state. */
@Composable
internal fun CanvasStage(controller:StageController,label:String,summaryId:String="studio-stage-summary") {
    val palette=ColorMode.current.toSitePalette()
    SideEffect{controller.setPalette(palette.accent.toString(),palette.borderStrong.toString())}
    Div(attrs={
        tabIndex(0);attr("role","group");attr("aria-roledescription",tr("studio.editor.canvas_stage.design_canvas","design canvas"))
        attr("aria-label",label);attr("aria-describedby",summaryId);attr("data-studio-region","stage")
        style{
            property("position","relative");property("width","100%");property("height","100%");property("min-height","240px")
            property("touch-action","none");property("user-select","none");property("contain","strict");property("overscroll-behavior","contain")
        }
        ref{host->controller.attach(host);onDispose{controller.detach()}}
    }){
        Canvas(attrs={classes("scene");attr("aria-hidden","true");style{property("position","absolute");property("inset","0")}})
        Canvas(attrs={classes("overlay");attr("aria-hidden","true");style{property("position","absolute");property("inset","0")}})
    }
}

/** DOM mirrors and inline editing may recompose on commits without remounting either canvas. */
@Composable
internal fun StageDomOverlays(state:StudioUiState,controller:StageController) {
    val selectionSummary = state.selected.joinToString { layer ->
        val box = layer.base.box
        trf("studio.selection_geometry", "{name}, {type}, x {x}, y {y}, {width} by {height}",
            "name" to layerDisplayName(layer), "type" to layerTypeName(layer), "x" to box.x, "y" to box.y, "width" to box.w, "height" to box.h)
    }
    Div(attrs={id("studio-stage-summary");style{property("position","absolute");property("width","1px");property("height","1px");property("overflow","hidden");property("clip-path","inset(50%)")}}){
        org.jetbrains.compose.web.dom.Text(tf("studio.stage_summary","{count} layers. Selected: {selection}",
            "count" to allLayers(state.layers).size,"selection" to selectionSummary))
        state.caption?.fit?.let { fit -> org.jetbrains.compose.web.dom.Text(
            if (fit is dev.lingvo.shared.screenshots.text.FitResult.Refused)
                tf("studio.summary_no_fit", "Does not fit at {size} px", "size" to fit.layout.size)
            else tf("studio.summary_fit", "Fits at {size} px", "size" to fit.layout.size)) }
    }
    if(state.inlineEditing)state.caption?.let { caption ->
        val layer = state.selected.filterIsInstance<dev.lingvo.shared.screenshots.doc.TextLayer>().find { it.base.id == caption.layer.base.id } ?: caption.layer
        InlineTextEditor(caption.copy(layer = layer), controller.viewport, kotlinx.browser.window.innerWidth, controller::dispatch,
            EngineEditorGeometry.world(state.page.copy(layers = state.layers), layer.base.id), fallback = state.fontStatus != null)
    }
}

/** Repair messages occupy a layout row above the canvas, never the artboard or bottom rail. */
@Composable
internal fun StageInputNotices(state: StudioUiState, dispatch: (StudioIntent) -> Unit) {
    if (state.captureAvailable == false) Div(attrs = { attr("role", "note") }) {
        org.jetbrains.compose.web.dom.Text(tf("studio.capture_missing", "No {language} capture yet",
            "language" to (state.languages.find { it.locale == state.view.locale }?.label ?: state.view.locale)))
        StudioButton(t("studio.editor.canvas_stage.upload", "Upload"), state.editable) { dispatch(StudioIntent.UploadCapture) }
        StudioButton(t("studio.editor.canvas_stage.ask_an_agent", "Ask an agent"), state.editable) { dispatch(StudioIntent.AskAgent) }
    }
    state.caption?.takeIf { it.value.isEmpty() }?.let { caption -> Div(attrs = { attr("role", "note") }) {
        org.jetbrains.compose.web.dom.Text(tf("studio.caption_missing", "No {language} caption yet. Write one here, or pre-translate the captions.", "language" to caption.language))
        org.jetbrains.compose.web.dom.A(href = "/app/projects/${encodeURIComponent(caption.project)}/pretranslate") {
            org.jetbrains.compose.web.dom.Text(t("studio.editor.canvas_stage.pre_translate_captions", "Pre-translate captions"))
        }
    } }
}

private external fun encodeURIComponent(value: String): String
